import { deviceLocal } from '@/privacy/deviceStorage';
import {
  DEFAULT_LOCALE,
  PSEUDO_LOCALE,
  isFormatRegionChoice,
  isLocaleChoice,
  negotiateLocale,
  type FormatRegionChoiceType,
  type AnyLocaleType,
  type LocaleChoiceType,
} from '@featit/shared/i18n/core';
import { I18N_TEST_LOCALES } from '@/buildConstants';
import type { HeldSentenceType } from '@/services/actionFailure';

/**
 * Which language the app shows (T-0472, `docs/concept-internationalisation.md` §4.2): the person's saved
 * choice, else the browser's first language FEATit speaks, else English. The choice is the person's, kept on
 * the server (`preferences.locale`) so it follows them to every device, and cached on this device so a launch
 * reads it without a request. Choosing applies at once and is then saved; a refused save puts the last choice
 * back and says so (`languageSync.ts`, a chunk of its own: it is the part that talks to the server, and the entry
 * needs only the choice, the cache and the browser's languages). With nobody signed in a choice is this device's, and the person's first sign-in keeps it
 * when the account has none.
 */
export const LOCALE_STORAGE_KEY = 'featit:locale';
/** F-0615: the picked region of dates and numbers, cached the same way; absent is "follow this device". */
export const FORMAT_REGION_STORAGE_KEY = 'featit:format-region';
/** A development or end-to-end build can also take a locale from here (`en-XA`, the pseudo-locale). */
export const I18N_OVERRIDE_KEY = 'featit:i18n-override';

type StorageType = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ILanguageSnapshot {
  /** The person's choice, or `null`: follow the browser. */
  choice: LocaleChoiceType;
  /** F-0615: the picked region of dates and numbers, or `null`: follow this device. */
  region: FormatRegionChoiceType;
  saving: boolean;
  /** Why the last save failed, for the Settings screen: kept, and worded where it is drawn. */
  error: HeldSentenceType | null;
  /** Why the last save of the region failed, worded where it is drawn like `error`. */
  regionError: HeldSentenceType | null;
}

export class LanguagePreference {
  private snapshot: ILanguageSnapshot;
  private listeners = new Set<() => void>();

  constructor(
    private readonly options: {
      storage: () => StorageType | null;
      browserLanguages: () => readonly string[];
    },
  ) {
    this.snapshot = {
      choice: this.cached(),
      region: this.cachedRegion(),
      saving: false,
      error: null,
      regionError: null,
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ILanguageSnapshot => this.snapshot;

  /** The language to show now. */
  resolved(): AnyLocaleType {
    if (I18N_TEST_LOCALES) {
      try {
        const override = this.options.storage()?.getItem(I18N_OVERRIDE_KEY);
        if (override === PSEUDO_LOCALE) return PSEUDO_LOCALE;
      } catch {
        // A browser that refuses storage has no override.
      }
    }
    return this.snapshot.choice ?? negotiateLocale(this.options.browserLanguages());
  }

  /** The person's choice, from the server or from a choice just made: applied and cached. */
  adopt(choice: LocaleChoiceType): void {
    this.set({ choice });
    this.cache(choice);
  }

  /** F-0615: the picked region, from the server or just picked: applied and cached. */
  adoptRegion(region: FormatRegionChoiceType): void {
    this.set({ region });
    try {
      const storage = this.options.storage();
      if (region === null) storage?.removeItem(FORMAT_REGION_STORAGE_KEY);
      else storage?.setItem(FORMAT_REGION_STORAGE_KEY, region);
    } catch {
      // A browser that refuses storage keeps the region for this page only.
    }
  }

  /** What the Settings screen shows beside the choice: a save in flight, or why one failed. */
  status(change: Partial<Pick<ILanguageSnapshot, 'saving' | 'error' | 'regionError'>>): void {
    this.set(change);
  }

  private set(change: Partial<ILanguageSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...change };
    for (const listener of this.listeners) listener();
  }

  private cached(): LocaleChoiceType {
    try {
      const stored = this.options.storage()?.getItem(LOCALE_STORAGE_KEY);
      return isLocaleChoice(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  private cachedRegion(): FormatRegionChoiceType {
    try {
      const stored = this.options.storage()?.getItem(FORMAT_REGION_STORAGE_KEY) ?? null;
      return isFormatRegionChoice(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  private cache(choice: LocaleChoiceType): void {
    try {
      const storage = this.options.storage();
      if (choice === null) storage?.removeItem(LOCALE_STORAGE_KEY);
      else storage?.setItem(LOCALE_STORAGE_KEY, choice);
    } catch {
      // A browser that refuses storage keeps the choice for this page only.
    }
  }
}

/** The page's own: the server's setting, this browser's storage and its languages. */
export function createLanguagePreference(): LanguagePreference {
  return new LanguagePreference({
    storage: () => {
      try {
        return typeof window === 'undefined' ? null : deviceLocal;
      } catch {
        return null;
      }
    },
    browserLanguages: () =>
      typeof navigator === 'undefined'
        ? [DEFAULT_LOCALE]
        : navigator.languages?.length
          ? navigator.languages
          : [navigator.language ?? DEFAULT_LOCALE],
  });
}
